// ============================================================================
// SETUP GATE (ADR-0048 §3) — shown by a transaction screen INSTEAD of its form
// when the capability it needs is not ready: the missing items, each a link to
// the exact screen. The server enforces the same gate on Post (SETUP_INCOMPLETE),
// so this is convenience, not the rule.
//
//   const gate = useSetupGate('INVOICING');
//   const handleAdd = async () => { if (!(await gate.ensureReady())) return; openForm(); };
//   ...
//   <SetupGateModal gate={gate} />
// ============================================================================

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { setupService } from 'api/services';
import { blockingChecks, type SetupCapabilityCode, type SetupCheckDto, type SetupReadinessDto } from 'api/types';
import { useNavigation } from 'contexts/NavigationContext';

export interface SetupGate {
  capability: SetupCapabilityCode;
  /** True when the capability is ready; otherwise opens the gate and returns false. */
  ensureReady: () => Promise<boolean>;
  blocking: SetupCheckDto[];
  open: boolean;
  close: () => void;
  refresh: () => Promise<boolean>;
}

export const useSetupGate = (capability: SetupCapabilityCode): SetupGate => {
  const [blocking, setBlocking] = useState<SetupCheckDto[]>([]);
  const [open, setOpen] = useState(false);

  const evaluate = useCallback(async (): Promise<boolean> => {
    let readiness: SetupReadinessDto;
    try {
      readiness = await setupService.getReadiness();
    } catch (err: any) {
      // The gate must never lock a working tenant out because the check itself failed:
      // let the form open and the server say no if it must.
      notificationService.toast.warning(err?.response?.data?.message || 'Could not check the setup; opening anyway.');
      return true;
    }
    const missing = blockingChecks(readiness, capability);
    setBlocking(missing);
    return missing.length === 0;
  }, [capability]);

  const ensureReady = useCallback(async () => {
    const ready = await evaluate();
    if (!ready) setOpen(true);
    return ready;
  }, [evaluate]);

  const refresh = useCallback(async () => {
    const ready = await evaluate();
    if (ready) setOpen(false);
    return ready;
  }, [evaluate]);

  return { capability, ensureReady, blocking, open, close: () => setOpen(false), refresh };
};

const CAPABILITY_VERB: Record<SetupCapabilityCode, string> = {
  COMPANY: 'use this entity',
  BOOKS: 'post to the books',
  TAX: 'compute tax',
  INVOICING: 'invoice',
  PURCHASING: 'record supplier bills',
  BANKING: 'record receipts and payments',
  INVENTORY: 'move stock',
};

export const SetupGateModal: React.FC<{ gate: SetupGate }> = ({ gate }) => {
  const { openScreen } = useNavigation();
  if (!gate.open) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={gate.close} />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div>
            <h2 className="modal-title">Before you can {CAPABILITY_VERB[gate.capability]}</h2>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {gate.blocking.length} required setting{gate.blocking.length === 1 ? '' : 's'} still missing for this entity.
            </div>
          </div>
          <button className="modal-close" onClick={gate.close}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {gate.blocking.map(c => (
              <li key={c.key} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.6rem 0.75rem', borderRadius: '0.5rem', background: 'var(--bg-secondary, #f8fafc)', border: '1px solid var(--border-color, #e2e8f0)' }}>
                <span style={{ color: 'var(--danger-color, #dc2626)', marginTop: 2 }}><IconSystem.alert size={16} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{c.title} <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{c.key}</span></div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{c.message}</div>
                </div>
                {c.screenCode && (
                  <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    onClick={() => { openScreen(c.screenCode!); gate.close(); }}>
                    Open
                  </button>
                )}
              </li>
            ))}
          </ul>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
            The full list, by stage, is under <strong>Administration → Setup</strong>. Items tick themselves as you configure them.
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={() => { openScreen('setup-checklist'); gate.close(); }}>Open Setup</button>
          <button className="btn btn-secondary" onClick={() => { gate.refresh().then(ok => { if (ok) notificationService.toast.success('Setup complete — you can continue.'); }); }}>Re-check</button>
          <button className="btn btn-primary" onClick={gate.close}>Close</button>
        </div>
      </div>
    </>
  );
};

export default SetupGateModal;
